import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Added for navigation
import { ChevronRight, ChevronDown, Target } from 'lucide-react';
import Image1 from "../../assets/healthcare.webp";
import Image2 from "../../assets/healthcare2.webp";

const Healthcare = () => {
    const [openIndex, setOpenIndex] = useState(0);
    const navigate = useNavigate(); // Initialize navigation
    const handleSignupClick = () => {
        navigate("/login?mode=signup");
    };
    const solutions = [
        {
            title: "Appointment Reminders & Scheduling",
            description: "Reduce no-show rates by up to 30% with automated SMS reminders. Patients can confirm, cancel, or reschedule appointments instantly via two-way messaging."
        },
        {
            title: "Instant Lab Results & Reports",
            description: "Securely notify patients the moment their results are ready. Our high-priority gateway ensures sensitive medical updates reach patients without delay."
        },
        {
            title: "Emergency Alerts & Staff Coordination",
            description: "Broadcast critical updates to medical staff in seconds. Whether it's shift changes or emergency protocols, Eurofox provides a 99.9% uptime guarantee for clinical reliability."
        },
        {
            title: "Prescription & Refill Notifications",
            description: "Improve medication adherence by sending automated alerts when prescriptions are ready for pickup or when it's time for a refill request."
        },
        {
            title: "Secure Telehealth Links",
            description: "Simplify the virtual care experience by delivering encrypted telehealth meeting links directly to the patient's mobile device via SMS."
        }
    ];

    // Healthcare-specific workflow tags
    const row1 = ["Book Appointment", "Insurance Verification", "Pre-Visit Instructions", "Check-in Confirmation", "Telehealth Link", "Doctor Consultation"];
    const row2 = ["Lab Results Ready", "Prescription Refill", "Follow-up Scheduled", "Health Tips", "Discharge Instructions", "Patient Feedback"];

    return (
        <div className="font-sans text-slate-900 overflow-x-hidden">
            {/* Premium Animation Styles */}
            <style>
                {`
                    @keyframes scroll-left {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    @keyframes scroll-right {
                        0% { transform: translateX(-50%); }
                        100% { transform: translateX(0); }
                    }
                    .animate-scroll-left {
                        display: flex;
                        width: max-content;
                        animation: scroll-left 40s linear infinite;
                    }
                    .animate-scroll-right {
                        display: flex;
                        width: max-content;
                        animation: scroll-right 40s linear infinite;
                    }
                    .pause-on-hover:hover {
                        animation-play-state: paused;
                    }
                `}
            </style>

            {/* --- SECTION 1: HERO SECTION --- */}
            <section className="bg-[#EEF2FC] flex flex-col md:flex-row items-center justify-between px-6 py-10 md:px-12 lg:px-24 min-h-[600px]">

                {/* LEFT CONTENT */}
                <div className="w-full md:w-1/2 mb-8 md:mb-0 md:pr-10 
                  pt-4 md:pt-1 
                  -mt-6 md:mt-0 
                  flex flex-col items-start">

                    <h1 className="text-3xl sm:text-4xl md:text-5xl 
                   font-medium text-slate-900 
                   leading-tight tracking-tight 
                   mb-4 md:mb-6">
                        Fast Alerts, Better Care — Healthcare Messages Everywhere
                    </h1>

                    <p className="text-base sm:text-lg text-slate-700 
                  mb-6 md:mb-8 leading-relaxed">
                        Deliver time-critical medical updates and appointment notifications through our high-priority SMS gateway.
                        Bridge the communication gap between providers and patients with secure, instantaneous messaging built for clinical reliability.
                    </p>

                    <button
                        onClick={() => navigate('/contact')}
                        className="inline-flex items-center gap-2 px-6 py-3 text-white bg-slate-900 rounded-lg transition duration-300 shadow-md"
                    >
                        Request a Demo
                        <ChevronRight className="w-5 h-5 animate-bounce" />
                    </button>
                </div>

                {/* RIGHT IMAGE */}
                <div className="w-full md:w-1/2 flex justify-center 
                  -mt-6 md:mt-0">
                    <img
                        src={Image1}
                        alt="Healthcare SMS Solutions Illustration"
                        className="w-full max-w-sm sm:max-w-md md:max-w-lg 
                 object-contain 
                 h-auto md:h-[500px] 
                 relative md:left-[1px] md:-top-[42px]"
                    />
                </div>

            </section>
            {/* --- SECTION 2: HEALTHCARE PROCESSES --- */}
            <section className="bg-[#EEF2FC] py-14 md:py-20 lg:py-32 -mt-10 md:-mt-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-12">

                    {/* HEADING */}
                    <div className="text-center max-w-4xl mx-auto mb-10 md:mb-16 lg:mb-24">
                        <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-6xl 
                     font-medium text-slate-900 leading-tight">
                            How Eurofox can help <br />
                            <span className="text-[#F46653]">
                                Healthcare processes
                            </span>
                        </h2>
                    </div>

                    {/* CONTENT */}
                    <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12 lg:gap-16">

                        {/* IMAGE */}
                        <div className="lg:w-5/12 w-full">
                            <div className="relative rounded-2xl overflow-hidden shadow-lg 
                        w-full h-[240px] sm:h-[300px] lg:h-[530px]">
                                <img
                                    src={Image2}
                                    alt="Eurofox Healthcare Process"
                                    className="w-full h-full object-cover object-center"
                                />
                            </div>
                        </div>

                        {/* ACCORDION */}
                        <div className="lg:w-7/12 w-full space-y-3 sm:space-y-4">
                            {solutions.map((item, index) => (
                                <div
                                    key={index}
                                    className="bg-white border border-slate-200 
                       rounded-xl lg:rounded-2xl 
                       shadow-sm transition-all duration-300"
                                >
                                    <button
                                        onClick={() =>
                                            setOpenIndex(openIndex === index ? -1 : index)
                                        }
                                        className="w-full flex items-center justify-between 
                         p-4 sm:p-5 lg:p-6 
                         text-left hover:bg-slate-50 
                         transition-colors rounded-xl lg:rounded-2xl"
                                    >
                                        <span className="font-medium text-slate-800 
                               text-base sm:text-lg lg:text-xl pr-4">
                                            {item.title}
                                        </span>

                                        <ChevronDown
                                            className={`w-5 h-5 text-slate-400 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? "rotate-180" : ""
                                                }`}
                                        />
                                    </button>

                                    <div
                                        className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index
                                                ? "max-h-60 opacity-100"
                                                : "max-h-0 opacity-0"
                                            }`}
                                    >
                                        <div className="px-4 sm:px-6 pb-5 text-slate-600 
                              text-sm sm:text-base lg:text-lg 
                              leading-relaxed border-t border-slate-50 pt-4">
                                            {item.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            </section>


            {/* --- SECTION 3: PREMIUM HEALTHCARE BENEFITS --- */}
            <section className="bg-[#EEF2FC] py-20 lg:py-28 -mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <span className="text-[#F46653] font-semibold text-base uppercase tracking-wider">Why Healthcare Leaders Choose Eurofox</span>
                        <h2 className="text-3xl lg:text-5xl font-medium text-slate-900 mt-3 mb-5 leading-tight">
                            Enterprise‑grade reliability with a human touch
                        </h2>
                        <p className="text-lg text-slate-600">
                            Our platform is built for the unique demands of healthcare — combining speed, security, and compliance to enhance every patient interaction.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                        {[
                            { title: "HIPAA-compliant messaging", desc: "Every message is encrypted end‑to‑end. We sign BAAs for compliance.", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
                            { title: "99.9% uptime SLA", desc: "Critical alerts delivered instantly with redundant carriers.", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
                            { title: "Two‑way patient engagement", desc: "Patients can reply to reminders or request refills directly.", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                            { title: "Smart workflow automation", desc: "Trigger reminders based on your EHR or practice system.", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
                            { title: "Unlimited segments", desc: "Send discharge instructions or lab reports as secure links.", icon: "M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" },
                            { title: "Detailed audit logs", desc: "Track every message for proof of delivery and compliance.", icon: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 01.586 1.414V19a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" }
                        ].map((benefit, i) => (
                            <div key={i} className="bg-slate-50 rounded-3xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                                <div className="w-14 h-14 bg-[#F46653]/10 rounded-2xl flex items-center justify-center mb-6">
                                    <svg className="w-7 h-7 text-[#F46653]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={benefit.icon} />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 mb-3">{benefit.title}</h3>
                                <p className="text-slate-600 leading-relaxed">{benefit.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
{/* --- SECTION 4: PREMIUM CONVERSATIONAL WORKFLOWS --- */}
<section className="bg-[#EEF2FC] py-16 md:py-24 lg:py-32 border-t border-slate-100 overflow-hidden">
    <div className="container mx-auto px-4 md:px-6 lg:px-12 text-center">
        {/* Heading: Scaled for mobile (text-3xl) vs desktop (text-6xl) */}
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-medium text-slate-900 mb-6 tracking-tight mt-0 lg:-mt-24 leading-tight">
            Faster messages, better care — <span className="text-[#F46653]">SMS solutions</span>
            <br className="hidden md:block" />
            <span className="text-[#F46653]"> for modern healthcare</span>
        </h2>

       {/* Scaled down top/bottom margins for mobile */}
        <div className="relative mt-10 mb-10 lg:mt-20 lg:mb-20 overflow-hidden">
            
            {/* Reduced gradient width on mobile so it doesn't cover the whole screen */}
            <div className="absolute inset-y-0 left-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-r from-[#EEF2FC] to-transparent"></div>
            <div className="absolute inset-y-0 right-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-l from-[#EEF2FC] to-transparent"></div>

            {/* Added 'flex' to ensure gap works, adjusted gaps for mobile */}
            <div className="animate-scroll-left flex gap-4 lg:gap-8 mb-4 lg:mb-8">
                {[...row1, ...row1].map((tag, idx) => (
                    <div
                        key={idx}
                        className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                    >
                        {tag}
                    </div>
                ))}
            </div>

            <div className="animate-scroll-right flex gap-4 lg:gap-8">
                {[...row2, ...row2].map((tag, idx) => (
                    <div
                        key={idx}
                        className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                    >
                        {tag}
                    </div>
                ))}
            </div>
        </div>
        
        {/* Paragraph: Adjusted leading and size for readability */}
        <p className="text-lg md:text-xl lg:text-2xl text-slate-600 font-normal max-w-4xl mx-auto leading-relaxed mb-10 md:mb-16 px-4">
            Empower medical teams and patients with intelligent SMS workflows that guide care, provide support, and continuously improve clinical outcomes.
        </p>

        {/* Button: Full width on tiny screens, inline-flex on larger ones */}
        <button
            onClick={handleSignupClick}
            className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 text-white bg-slate-900 rounded-xl transition duration-300 shadow-xl hover:shadow-2xl text-lg md:text-xl font-semibold"
        >
            Start Your Learning Journey
            <Target className="w-5 h-5 md:w-6 md:h-6" />
        </button>
    </div>
 </section>

        </div>
    );
};

export default Healthcare;